<template>
  <PreAuthPage :footer="found">
    <div class="sh-card__head">
      <LogoTile v-if="branding" :branding="branding" :alt="tenantName" :size="44" />
      <span class="unavailable__badge"><v-icon size="20" color="warning">cloud_off</v-icon></span>
      <h1 class="sh-card__title">{{ tenantName }}</h1>
      <p class="sh-card__subtitle">{{ message }}</p>
    </div>

    <!-- Provisioning finishes on its own within minutes; the visitor should
         not have to know that a reload is what re-resolves the app. -->
    <template v-if="provisioning">
      <v-btn class="unavailable__retry" color="primary" variant="outlined" block @click="reloadPage()">Check again</v-btn>
      <p v-if="autoChecking" class="sh-card__footnote">
        This page checks again on its own every {{ AUTO_RETRY_SECONDS }} seconds.
      </p>
    </template>
  </PreAuthPage>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, onMounted } from "vue";
import { useSessionStore } from "@/stores/session";
import { autoRetry, clearAutoRetries, MAX_AUTO_RETRIES, readAutoRetries, reloadPage } from "@/tenant/retry";
import LogoTile from "@/shell/LogoTile.vue";
import PreAuthPage from "@/shell/PreAuthPage.vue";

export const AUTO_RETRY_SECONDS = 30;

export default defineComponent({
  name: "Unavailable",
  components: { LogoTile, PreAuthPage },
  setup() {
    const session = useSessionStore();
    const found = computed(() => !!session.tenant?.found);
    const tenantName = computed(() => (found.value ? session.tenant?.name || "This app" : "Metaship"));
    const branding = computed(() => session.tenant?.branding ?? null);
    const provisioning = computed(() => found.value && session.tenant?.status === "PROVISIONING");
    const message = computed(() => {
      if (!found.value) return "This app is not available at this address.";
      switch (session.tenant?.status) {
        case "DISABLED":
          return "This app has been temporarily disabled.";
        case "PROVISIONING":
          return "This app is still being set up — check back shortly.";
        case "FAILED":
        case "DRAFT":
        case "DEPROVISIONING":
        case "DELETED":
        default:
          return "This app is not available right now.";
      }
    });

    // Automatic re-checks stop after MAX_AUTO_RETRIES (10 min) — a tab left
    // on an app wedged in PROVISIONING must not reload forever. The button
    // stays; a manual check never counts.
    const autoChecking = computed(
      () => provisioning.value && readAutoRetries(window.location.hostname) < MAX_AUTO_RETRIES,
    );
    let timer: number | undefined;
    onMounted(() => {
      if (!provisioning.value) {
        clearAutoRetries(window.location.hostname);
        return;
      }
      timer = window.setInterval(() => autoRetry(window.location.hostname, reloadPage), AUTO_RETRY_SECONDS * 1000);
    });
    onBeforeUnmount(() => {
      if (timer) window.clearInterval(timer);
    });

    return { found, tenantName, branding, message, provisioning, autoChecking, reloadPage, AUTO_RETRY_SECONDS };
  },
});
</script>

<style scoped>
.unavailable__badge {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: rgba(var(--v-theme-warning), 0.14);
}
.unavailable__retry {
  font-weight: 600;
}
</style>
